import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  CampusUnits,
  ChurchUnit,
  CampusStakeDetails,
  CampusWard,
  LocalWard,
  LocalWardAssignment,
  NewLocalWard,
  CampusStake,
  StakeLeader,
  CampusUnitAccess,
  CampusStakeDetailsForUpdate,
  UnitLoginResult
} from '../../models';
import { AbstractDataService } from './abstract-data-service';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

@Injectable()
export class ChurchUnitsDataService extends AbstractDataService {
  private campusUnitsUrl = 'api/campus-units';
  private stakesUrl = 'api/campus-units/stakes';
  private churchUnitsUrl = 'api/church-units';
  private wardsUrl = 'api/campus-units/wards';

  // we aren't going to cache the campus wards, stakes, and "local" wards (those assigned to campus wards)
  // because these are actually likely to change (since this system is where their information gets changed)

  constructor(private http: HttpClient) {
    super();
  }

  /**
   * Gets the campus wards, stakes, and local wards assigned to campus wards for BYU-Idaho.
   */
  getCampusUnits(): Observable<CampusUnits> {
    return this.http.get<CampusUnits>(`${this.campusUnitsUrl}`).pipe(
      retry(2)
    );
  }

  /**
   * Gets the church unit by the specified unit number.
   */
  getChurchUnit(unitNumber: string): Observable<ChurchUnit> {
    return this.http
      .get<ChurchUnit>(`api/church-units/${unitNumber}`)
      .pipe(catchError(this.handleNotFound));
  }

  /**
   * Gets all of the campus stakes.
   */
  getCampusStakes(): Observable<CampusStake[]> {
    return this.http.get<CampusStake[]>(`${this.stakesUrl}`).pipe(
      retry(2));
  }

  getCampusWards(): Observable<CampusWard[]> {
    return this.http.get<CampusWard[]>(`${this.wardsUrl}`).pipe(
      retry(2)
    );
  }

  /**
   * Gets the wards that belong to the stake.
   */
  getStakeWards(campusStakeId: number): Observable<CampusWard[]> {
    return this.http.get<CampusWard[]>(
      `${this.stakesUrl}/${campusStakeId}/wards`
    );
  }

  /**
   * Gets the detailed information for the campus stake.
   */
  getCampusStakeDetails(campusStakeId: number): Observable<CampusStakeDetails> {
    return this.http
      .get<CampusStakeDetails>(`${this.stakesUrl}/${campusStakeId}`)
      .pipe(catchError(this.handleNotFound));
  }

  /**
   * Gets the detailed information for the campus stake, including information that only can be seen and updated for an update.
   */
  getCampusStakeDetailsForUpdate(campusStakeId: number): Observable<CampusStakeDetailsForUpdate> {
    return this.http
      .get<CampusStakeDetailsForUpdate>(`${this.stakesUrl}/for-update/${campusStakeId}`)
      .pipe(catchError(this.handleNotFound));
  }

  /**
   * Gets the South-East Idaho wards that can be assigned to campus singles wards.
   */
  getLocalWards(): Observable<LocalWard[]> {
    return this.http
      .get<LocalWard[]>(`${this.campusUnitsUrl}/local-wards`)
      .pipe(
        retry(2)
      );
  }

  /**
   * Gets the South-East Idaho ward (that's assigned to a campus ward) by its assignment's primary key.
   */
  getLocalWard(campusHomeWardId: number): Observable<LocalWard> {
    return this.http.get<LocalWard>(
      `${this.campusUnitsUrl}/local-wards/${campusHomeWardId}`
    );
  }

  /**
   * Creates a new local ward assignment.
   */
  createLocalWardAssignment(campusWardId: number, unitNumber: string): Observable<LocalWard> {
    return this.http.post<LocalWard>(`${this.campusUnitsUrl}/local-wards/assignments`,
        new NewLocalWard(campusWardId, unitNumber)
      )
      .pipe(
        catchError(this.handleBadRequest('Unable to create new assignment'))
      );
  }

  /**
   * Updates the ward assigned to the local wards.
   */
  updateLocalWardAssignment(campusWardId: number, campusHomeWardIds: number[]) {
    return this.http
      .put(
        `${this.campusUnitsUrl}/local-wards/assignments`,
        new LocalWardAssignment(campusHomeWardIds, campusWardId)
      )
      .pipe(
        catchError(this.handleBadRequest('Unable to update ward assignments'))
      );
  }

  /**
   * Clears the ward assigned to the local wards.
   */
  removeLocalWardAssignment(campusHomeWardIds: number[]) {
    // same API to clear, just don't pass a ward
    return this.http
      .put(
        `${this.campusUnitsUrl}/local-wards/assignments`,
        new LocalWardAssignment(campusHomeWardIds)
      )
      .pipe(
        catchError(this.handleBadRequest('Unable to remove ward assignments'))
      );
  }

  /**
   * Create a new campus ward.
   */
  createCampusStake(newCampusStakeDetails: CampusStakeDetailsForUpdate): Observable<CampusStakeDetailsForUpdate> {
    return this.http
      .post<CampusStakeDetailsForUpdate>(`${this.stakesUrl}`, newCampusStakeDetails)
      .pipe(catchError(this.handleBadRequest('Unable to create a new stake.')));
  }

  /**
   * Creates the stake leader's information, including the photograph.
   *
   * @param leader The leader's information
   * @param photo The leader's new photo, if any
   */
  createStakeLeader(leader: StakeLeader, photo: File): Observable<StakeLeader> {
    const formData = this.getLeaderFormData(Object.assign({}, leader), photo);
    return this.http.post<StakeLeader>(`${this.stakesUrl}/${leader.campusStakeId}/leaders`, formData)
      .pipe(
        catchError(
          this.handleBadRequest('Unable to update stake leader information.')
        )
      );
  }

  /**
   * Updates the stake leader's information, including the photograph.
   *
   * @param leader The leader's information
   * @param photo The leader's new photo, if any
   * @param removePhoto A flag letting us know if we want to remove the leader's current photo or not. This will trump the new photo.
   */
  updateStakeLeader(leader: StakeLeader, photo: File, removePhoto: boolean): Observable<StakeLeader> {
    const updateModel = Object.assign({ deleteExistingPhoto: false }, leader);
    updateModel.photoBase64 = null; // clear this out to just reduce data going over the wire

    if (removePhoto) {
      updateModel.deleteExistingPhoto = true;
    }

    const formData = this.getLeaderFormData(updateModel, photo);
    return this.http.put<StakeLeader>(`${this.stakesUrl}/${leader.campusStakeId}/leaders/${
          leader.campusStakeOfficeId
        }`,
        formData
      )
      .pipe(
        catchError(
          this.handleBadRequest('Unable to update stake leader information.')
        )
      );
  }

  /**
   * Deletes the stake leader's information.
   */
  deleteStakeLeader(leader: StakeLeader) {
    return this.http.delete(
      `${this.stakesUrl}/${leader.campusStakeId}/leaders/${
        leader.campusStakeOfficeId
      }`
    );
  }

  /**
   * Update a campus stake's information.
   */
  updateStakeInformation(campusStakeDetails: CampusStakeDetailsForUpdate): Observable<CampusStakeDetailsForUpdate> {
    return this.http.put<CampusStakeDetailsForUpdate>(`${this.stakesUrl}/${campusStakeDetails.campusStakeId}/information`,
        campusStakeDetails
      )
      .pipe(
        catchError(this.handleBadRequest('Unable to update stake information.'))
      );
  }

  /**
   * Updates stake info
   */
  updateCampusStake(campusStakeDetails: CampusStakeDetailsForUpdate): Observable<CampusStakeDetailsForUpdate> {
    return this.http.put<CampusStakeDetailsForUpdate>(`${this.stakesUrl}/${campusStakeDetails.campusStakeId}`,
        campusStakeDetails
      )
      .pipe(
        catchError(this.handleBadRequest('Unable to update stake information.'))
      );
  }

  /**
   *  "Deletes" campus stake
   */
  deleteCampusStake(campusStakeId: number) {
    return this.http.delete(`${this.stakesUrl}/${campusStakeId}`);
  }

  /**
   * Gets the form data to use to create or update a leader's information.
   *
   * @param model The model to send as json with the leader's information
   * @param photo The leader's photo
   */
  private getLeaderFormData(model: any, photo: File): FormData {
    const formData = new FormData();
    formData.append('leaderInformationJson', JSON.stringify(model));
    if (photo) {
      formData.append('newPhoto', photo, photo.name);
    }

    return formData;
  }

  /**
   * Campus unit login
   */
  unitLogIn(campusWard: CampusUnitAccess) {
    return this.http
      .post<UnitLoginResult>(`${this.churchUnitsUrl}/login`, campusWard)
      .pipe(catchError(this.handleBadRequest('Leader not authorized.')));
  }
}
